import { BadgeAlertIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { isForbidden, isOffline } from '@/shared/api'
import type { AppError } from '@/shared/api'
import { cn } from '@/shared/lib'
import { useErrorMessage } from '@/shared/lib/use-error-message'

import { Button } from './button'

type ErrorStateProps = {
  error: AppError
  onRetry?: () => void
  className?: string
}

/**
 * A failed read renders here, in the space where the data belongs. Never toast
 * a read failure: the toast disappears and leaves an unexplained empty screen.
 *
 * Same dashed plate as `EmptyState`, tinted danger — the copy names the cause
 * and then the consequence, and never apologises.
 */
export function ErrorState({ error, onRetry, className }: ErrorStateProps) {
  const { t } = useTranslation('common')
  const resolve = useErrorMessage()

  const title = isForbidden(error)
    ? t('errors.forbiddenTitle')
    : isOffline(error)
      ? t('errors.offlineTitle')
      : t('errors.title')

  return (
    <div
      role="alert"
      data-slot="error-state"
      className={cn(
        'flex flex-col items-center rounded-xl border border-dashed border-destructive/25 px-5 py-7 text-center',
        className,
      )}
    >
      <span className="mb-3.5 flex size-10 items-center justify-center rounded-full border border-destructive/25 bg-destructive/12 text-destructive-text [&_svg]:size-4">
        <BadgeAlertIcon />
      </span>

      <p className="text-h3 font-medium">{title}</p>
      <p className="mt-1.25 text-body text-muted-foreground">{resolve(error)}</p>

      {error.requestId !== undefined && (
        <p className="mt-1 font-mono text-label text-fg-disabled">{error.requestId}</p>
      )}

      {/* A 403 is not retryable — the credential is valid and the answer is no. */}
      {onRetry !== undefined && !isForbidden(error) && (
        <Button variant="secondary" size="sm" className="mt-3.5" onClick={onRetry}>
          {t('actions.retry')}
        </Button>
      )}
    </div>
  )
}
